import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Wind, Sun, Cloud, ArrowUpRight, ArrowDownRight, Minus, Download, RefreshCw, Calendar, Euro, Zap, ShoppingCart, DollarSign, Info, ChevronDown, X, ArrowRight } from 'lucide-react';
// --- Mock Data ---
const regionalPricing = [{
  region: 'Germany',
  price: 85,
  delta24h: 2.3,
  delta7d: -1.2,
  weatherImpact: 'wind',
  trend: 'up'
}, {
  region: 'France',
  price: 78,
  delta24h: -1.5,
  delta7d: 3.4,
  weatherImpact: 'nuclear',
  trend: 'down'
}, {
  region: 'Spain',
  price: 72,
  delta24h: -3.2,
  delta7d: -5.1,
  weatherImpact: 'solar',
  trend: 'down'
}, {
  region: 'UK',
  price: 92,
  delta24h: 4.1,
  delta7d: 2.8,
  weatherImpact: 'wind',
  trend: 'up'
}, {
  region: 'Italy',
  price: 98,
  delta24h: 1.2,
  delta7d: 4.5,
  weatherImpact: 'gas',
  trend: 'up'
}, {
  region: 'Netherlands',
  price: 84,
  delta24h: -0.5,
  delta7d: -2.1,
  weatherImpact: 'wind',
  trend: 'stable'
}, {
  region: 'Belgium',
  price: 81,
  delta24h: -1.8,
  delta7d: -1.5,
  weatherImpact: 'nuclear',
  trend: 'down'
}, {
  region: 'Nordics',
  price: 45,
  delta24h: 5.2,
  delta7d: 8.4,
  weatherImpact: 'hydro',
  trend: 'up'
}];
const priceTrends = {
  hourly: Array.from({
    length: 24
  }, (_, i) => ({
    time: `${i}:00`,
    DE: 80 + Math.random() * 20 - 10,
    FR: 75 + Math.random() * 15 - 7,
    ES: 70 + Math.random() * 25 - 12,
    UK: 90 + Math.random() * 18 - 9
  })),
  daily: Array.from({
    length: 7
  }, (_, i) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return {
      time: days[i],
      DE: 85 + Math.random() * 30 - 15,
      FR: 78 + Math.random() * 20 - 10,
      ES: 72 + Math.random() * 35 - 17,
      UK: 92 + Math.random() * 25 - 12
    };
  }),
  weekly: Array.from({
    length: 4
  }, (_, i) => ({
    time: `Week ${i + 1}`,
    DE: 88 + Math.random() * 40 - 20,
    FR: 80 + Math.random() * 30 - 15,
    ES: 75 + Math.random() * 45 - 22,
    UK: 95 + Math.random() * 35 - 17
  }))
};
const generationMix = [{
  name: 'Renewable',
  value: 45,
  fill: '#10b981'
}, {
  name: 'Gas',
  value: 30,
  fill: '#f97316'
}, {
  name: 'Nuclear',
  value: 15,
  fill: '#a855f7'
}, {
  name: 'Coal',
  value: 10,
  fill: '#64748b'
}];
const weatherCorrelation = Array.from({
  length: 24
}, (_, i) => ({
  time: `${i}:00`,
  wind: 5 + Math.sin(i / 3) * 3 + Math.random(),
  solar: i > 6 && i < 20 ? Math.sin((i - 6) / 14 * Math.PI) * 800 : 0,
  temp: 15 + Math.sin(i / 4) * 5,
  price: 80 - Math.sin(i / 3) * 10 - (i > 6 && i < 20 ? Math.sin((i - 6) / 14 * Math.PI) * 20 : 0) + Math.random() * 5
}));
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-50 rounded-xl p-4 shadow-2xl z-50" data-id="element-1417">
        <p className="text-xs font-semibold text-slate-900 mb-2" data-id="element-1418">{label}</p>
        <div className="space-y-1" data-id="element-1419">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-1420">
              <div className="flex items-center gap-2" data-id="element-1421">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-1422" />
                <span className="text-xs text-slate-600 capitalize" data-id="element-1423">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-slate-900" data-id="element-1424">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
const CustomPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-bold" data-id="element-1425">
      {`${(percent * 100).toFixed(0)}%`}
    </text>;
};
export function EuropePricingDashboard() {
  const [trendTimeframe, setTrendTimeframe] = useState<'hourly' | 'daily' | 'weekly'>('hourly');
  const [weatherVariable, setWeatherVariable] = useState<'wind' | 'solar' | 'temp'>('wind');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const sortedPricing = useMemo(() => {
    if (!sortConfig) return regionalPricing;
    return [...regionalPricing].sort((a, b) => {
      // @ts-ignore
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      // @ts-ignore
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);
  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };
  const getWeatherUnit = () => {
    switch (weatherVariable) {
      case 'wind':
        return 'm/s';
      case 'solar':
        return 'W/m²';
      case 'temp':
        return '°C';
    }
  };
  return <div className="min-h-screen p-6 space-y-6 pb-20" data-id="element-1426">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1427">
        <div data-id="element-1428">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-1429">
            Europe Pricing Overview
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base" data-id="element-1430">
            Real-time power pricing across European markets
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1431">
          {/* Date Picker */}
          <div className="relative" data-id="element-1432">
            <button onClick={() => setShowDatePicker(!showDatePicker)} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-1433">
              <Calendar size={16} data-id="element-1434" />
              <span data-id="element-1435">{formatDate(selectedDate)}</span>
              <ChevronDown size={14} className={`transition-transform ${showDatePicker ? 'rotate-180' : ''}`} data-id="element-1436" />
            </button>

            <AnimatePresence data-id="element-1437">
              {showDatePicker && <>
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => setShowDatePicker(false)} className="fixed inset-0 z-40" data-id="element-1438" />
                  <motion.div initial={{
                opacity: 0,
                y: -10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -10,
                scale: 0.95
              }} className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-id="element-1439">
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between" data-id="element-1440">
                      <h3 className="text-sm font-bold text-slate-900" data-id="element-1441">
                        Select Date
                      </h3>
                      <button onClick={() => setShowDatePicker(false)} className="p-1 hover:bg-slate-100 rounded transition-colors" data-id="element-1442">
                        <X size={14} className="text-slate-500" data-id="element-1443" />
                      </button>
                    </div>
                    <div className="p-3 space-y-1" data-id="element-1444">
                      {[0, 1, 2, 3, 7, 14, 30].map(daysAgo => {
                    const date = new Date();
                    date.setDate(date.getDate() - daysAgo);
                    return <button key={daysAgo} onClick={() => {
                      setSelectedDate(date);
                      setShowDatePicker(false);
                    }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedDate.toDateString() === date.toDateString() ? 'bg-pink-50 text-pink-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-1445">
                            {formatDate(date)}
                          </button>;
                  })}
                    </div>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>

          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-1446">
            <Download size={16} data-id="element-1447" />
            <span data-id="element-1448">Export</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-[#ff006b] text-white rounded-lg text-sm font-medium hover:bg-[#ff006b]/90 transition-colors shadow-sm shadow-pink-500/20" data-id="element-1449">
            <RefreshCw size={16} data-id="element-1450" />
            <span data-id="element-1451">Refresh</span>
          </button>
        </div>
      </div>

      {/* Highlight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1452">
        {/* Average Price Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col h-full" data-id="element-1453">
          <div className="flex items-center justify-between mb-4" data-id="element-1454">
            <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-1455">
              <Euro size={16} data-id="element-1456" />
              Avg. EU Price
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-1457">
              Normal Range
            </span>
          </div>
          <div className="mb-6" data-id="element-1458">
            <div className="flex items-baseline gap-2 mb-1" data-id="element-1459">
              <span className="text-3xl font-bold text-slate-900" data-id="element-1460">€85</span>
              <span className="text-sm font-medium text-slate-500" data-id="element-1461">/MWh</span>
            </div>
            <p className="text-sm font-medium text-emerald-600 flex items-center gap-1" data-id="element-1462">
              <TrendingDown size={14} data-id="element-1463" />
              2.4% below 7d avg
            </p>
          </div>
          <div className="flex-1" data-id="element-1464">
            <div className="relative h-2 bg-slate-100 rounded-full mb-2" data-id="element-1465">
              <div className="absolute top-0 bottom-0 left-[20%] right-[20%] bg-slate-200 rounded-sm" data-id="element-1466" />
              <div className="absolute top-1/2 -translate-y-1/2 left-[45%] w-3 h-3 bg-emerald-500 border-2 border-white rounded-full shadow-sm z-10" data-id="element-1467" />
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 font-medium mb-4" data-id="element-1468">
              <span data-id="element-1469">€40</span>
              <span className="text-slate-500" data-id="element-1470">Typical: €60-€110</span>
              <span data-id="element-1471">€140</span>
            </div>
          </div>
          <button className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg transition-all" data-id="element-1472">
            View Details
            <ArrowRight size={14} data-id="element-1473" />
          </button>
        </motion.div>

        {/* Best Buy Opportunity */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col h-full" data-id="element-1474">
          <div className="flex items-center justify-between mb-4" data-id="element-1475">
            <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-1476">
              <ShoppingCart size={16} data-id="element-1477" />
              Best Buy Opportunity
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100" data-id="element-1478">
              Strong Signal
            </span>
          </div>
          <div className="flex-1" data-id="element-1479">
            <div className="flex justify-between items-end mb-2" data-id="element-1480">
              <div data-id="element-1481">
                <span className="text-2xl font-bold text-slate-900" data-id="element-1482">Spain</span>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-id="element-1483">
                  <Sun size={12} className="text-amber-500" data-id="element-1484" />
                  High solar output
                </div>
              </div>
              <div className="text-right" data-id="element-1485">
                <div className="text-xl font-bold text-emerald-600" data-id="element-1486">€72</div>
                <div className="text-xs text-slate-400" data-id="element-1487">/MWh</div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 mb-3" data-id="element-1488">
              <div className="flex justify-between text-xs mb-1" data-id="element-1489">
                <span className="text-slate-500" data-id="element-1490">Window</span>
                <span className="font-medium text-slate-900" data-id="element-1491">
                  Next 12-18 hours
                </span>
              </div>
              <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden" data-id="element-1492">
                <div className="bg-emerald-500 h-full w-3/4 rounded-full" data-id="element-1493" />
              </div>
            </div>
          </div>
          <button className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg transition-all" data-id="element-1494">
            View Details
            <ArrowRight size={14} data-id="element-1495" />
          </button>
        </motion.div>

        {/* Best Sell Opportunity */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col h-full" data-id="element-1496">
          <div className="flex items-center justify-between mb-4" data-id="element-1497">
            <h3 className="text-sm font-semibold text-slate-500 flex items-center gap-2" data-id="element-1498">
              <DollarSign size={16} data-id="element-1499" />
              Best Sell Opportunity
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100" data-id="element-1500">
              Medium Signal
            </span>
          </div>
          <div className="flex-1" data-id="element-1501">
            <div className="flex justify-between items-end mb-2" data-id="element-1502">
              <div data-id="element-1503">
                <span className="text-2xl font-bold text-slate-900" data-id="element-1504">
                  Germany
                </span>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1" data-id="element-1505">
                  <Wind size={12} className="text-slate-400" data-id="element-1506" />
                  Low wind forecast
                </div>
              </div>
              <div className="text-right" data-id="element-1507">
                <div className="text-xl font-bold text-amber-600" data-id="element-1508">€98</div>
                <div className="text-xs text-slate-400" data-id="element-1509">/MWh</div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 mb-3" data-id="element-1510">
              <div className="flex justify-between text-xs mb-1" data-id="element-1511">
                <span className="text-slate-500" data-id="element-1512">Window</span>
                <span className="font-medium text-slate-900" data-id="element-1513">
                  Tomorrow 14:00-18:00
                </span>
              </div>
              <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden" data-id="element-1514">
                <div className="bg-amber-500 h-full w-1/2 rounded-full ml-auto" data-id="element-1515" />
              </div>
            </div>
          </div>
          <button className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg transition-all" data-id="element-1516">
            View Details
            <ArrowRight size={14} data-id="element-1517" />
          </button>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-1518">
        {/* Regional Pricing Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="lg:col-span-4 bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-1519">
          <div className="p-5 border-b border-slate-100" data-id="element-1520">
            <h3 className="font-bold text-slate-900" data-id="element-1521">Regional Pricing</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-1522">
              Real-time market data across zones
            </p>
          </div>
          <div data-id="element-1523">
            <table className="w-full text-left border-collapse" data-id="element-1524">
              <thead data-id="element-1525">
                <tr className="bg-slate-50/50 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100" data-id="element-1526">
                  <th className="px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('region')} data-id="element-1527">
                    Region
                  </th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('price')} data-id="element-1528">
                    Price
                  </th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('delta24h')} data-id="element-1529">
                    24h Δ
                  </th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('delta7d')} data-id="element-1530">
                    7d Δ
                  </th>
                  <th className="px-4 py-3 text-center" data-id="element-1531">Impact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm" data-id="element-1532">
                {sortedPricing.map(row => <tr key={row.region} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1533">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1534">
                      {row.region}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700" data-id="element-1535">
                      €{row.price}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${row.delta24h > 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1536">
                      {row.delta24h > 0 ? '+' : ''}
                      {row.delta24h}%
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${row.delta7d > 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1537">
                      {row.delta7d > 0 ? '+' : ''}
                      {row.delta7d}%
                    </td>
                    <td className="px-4 py-3 flex justify-center" data-id="element-1538">
                      <div className="p-1.5 rounded-md bg-slate-100 text-slate-500 group-hover:bg-white group-hover:shadow-sm transition-all" title={`${row.weatherImpact} impact`} data-id="element-1539">
                        {row.weatherImpact === 'wind' && <Wind size={14} data-id="element-1540" />}
                        {row.weatherImpact === 'solar' && <Sun size={14} data-id="element-1541" />}
                        {row.weatherImpact === 'nuclear' && <Zap size={14} data-id="element-1542" />}
                        {row.weatherImpact === 'gas' && <TrendingUp size={14} data-id="element-1543" />}
                        {row.weatherImpact === 'hydro' && <Cloud size={14} data-id="element-1544" />}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Price Trends Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-8 bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1545">
          <div className="flex items-center justify-between mb-6" data-id="element-1546">
            <div data-id="element-1547">
              <h3 className="font-bold text-slate-900" data-id="element-1548">Price Trends</h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-1549">
                Comparative analysis across key markets
              </p>
            </div>
            <div className="flex bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/50" data-id="element-1550">
              {(['hourly', 'daily', 'weekly'] as const).map(t => <button key={t} onClick={() => setTrendTimeframe(t)} className={`px-3 py-1 rounded-md text-xs font-medium transition-all capitalize ${trendTimeframe === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1551">
                  {t}
                </button>)}
            </div>
          </div>
          <div className="flex-1 min-h-[300px]" data-id="element-1552">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1553">
              <LineChart data={priceTrends[trendTimeframe]} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 0
            }} data-id="element-1554">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-1555" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#94a3b8'
              }} dy={10} data-id="element-1556" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#94a3b8'
              }} dx={-10} label={{
                value: '€/MWh',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#64748b'
                }
              }} data-id="element-1557" />
                <Tooltip content={<CustomTooltip data-id="element-1559" />} data-id="element-1558" />
                <Legend iconType="circle" wrapperStyle={{
                fontSize: '12px',
                paddingTop: '10px'
              }} data-id="element-1560" />
                <Line type="monotone" dataKey="DE" name="Germany" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{
                r: 4
              }} data-id="element-1561" />
                <Line type="monotone" dataKey="FR" name="France" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{
                r: 4
              }} data-id="element-1562" />
                <Line type="monotone" dataKey="ES" name="Spain" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{
                r: 4
              }} data-id="element-1563" />
                <Line type="monotone" dataKey="UK" name="UK" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{
                r: 4
              }} data-id="element-1564" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1565">
        {/* Market Composition */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1566">
          <div className="mb-4" data-id="element-1567">
            <h3 className="font-bold text-slate-900" data-id="element-1568">Generation Mix</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-1569">
              EU-wide power source distribution
            </p>
          </div>
          <div className="flex-1 min-h-[250px] flex items-center justify-center" data-id="element-1570">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1571">
              <PieChart data-id="element-1572">
                <Pie data={generationMix} cx="50%" cy="50%" labelLine={false} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1573">
                  {generationMix.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-1574" />)}
                </Pie>
                <Tooltip content={<CustomTooltip data-id="element-1576" />} data-id="element-1575" />
                <Legend layout="vertical" verticalAlign="middle" align="right" iconType="circle" wrapperStyle={{
                fontSize: '12px'
              }} formatter={(value, entry: any) => `${value} (${entry.payload.value}%)`} data-id="element-1577" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Weather Correlation */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1578">
          <div className="flex items-center justify-between mb-6" data-id="element-1579">
            <div data-id="element-1580">
              <h3 className="font-bold text-slate-900" data-id="element-1581">Weather Impact</h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-1582">
                Correlation between weather and price
              </p>
            </div>
            <div className="flex bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/50" data-id="element-1583">
              <button onClick={() => setWeatherVariable('wind')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${weatherVariable === 'wind' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1584">
                Wind
              </button>
              <button onClick={() => setWeatherVariable('solar')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${weatherVariable === 'solar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1585">
                Solar
              </button>
              <button onClick={() => setWeatherVariable('temp')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${weatherVariable === 'temp' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1586">
                Temp
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-[250px]" data-id="element-1587">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1588">
              <AreaChart data={weatherCorrelation} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 0
            }} data-id="element-1589">
                <defs data-id="element-1590">
                  <linearGradient id="colorWeather" x1="0" y1="0" x2="0" y2="1" data-id="element-1591">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.1} data-id="element-1592" />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-1593" />
                  </linearGradient>
                  <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1" data-id="element-1594">
                    <stop offset="5%" stopColor="#ff006b" stopOpacity={0.1} data-id="element-1595" />
                    <stop offset="95%" stopColor="#ff006b" stopOpacity={0} data-id="element-1596" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-1597" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#94a3b8'
              }} dy={10} data-id="element-1598" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#06b6d4'
              }} dx={-10} label={{
                value: getWeatherUnit(),
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#06b6d4'
                }
              }} data-id="element-1599" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#ff006b'
              }} dx={10} label={{
                value: '€/MWh',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#ff006b'
                }
              }} data-id="element-1600" />
                <Tooltip content={<CustomTooltip data-id="element-1602" />} data-id="element-1601" />
                <Legend iconType="circle" wrapperStyle={{
                fontSize: '12px',
                paddingTop: '10px'
              }} data-id="element-1603" />
                <Area yAxisId="left" type="monotone" dataKey={weatherVariable} name={`${weatherVariable.charAt(0).toUpperCase() + weatherVariable.slice(1)} (${getWeatherUnit()})`} stroke="#06b6d4" fill="url(#colorWeather)" strokeWidth={2} data-id="element-1604" />
                <Area yAxisId="right" type="monotone" dataKey="price" name="Price (€/MWh)" stroke="#ff006b" fill="url(#colorPrice)" strokeWidth={2} data-id="element-1605" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>;
}